// One driver's outstanding cash, item by item, and a way to close an item with
// the slip or the receipt that answers it.
//
// WHY ITEMS AND NOT A TOTAL. The By driver row says how much. A driver can only
// ever answer "which shift" and "which slip", so a total is a figure nobody can
// act on. Luyanda asked for this on 26 October 2026 looking at Okhela Mngoma: the
// list of what is outstanding across all his deposits, so we can see where the
// money is, with a manager able to upload the evidence and mark it deposited or
// spent on petrol.
//
// THE THREE KINDS ARE NEVER ADDED INTO ONE FIGURE. On the day this was built
// Okhela showed R997 of cash his declarations did not account for, R70,800 of
// declared deposits the statement does not show, and R36,445 of cash petrol no
// report supports. The first is a question for him. The second may sit in an
// account Fleetly never loaded, and the third is mostly fuel reports that never
// recorded how a fill was paid. A single "owes R108,242" would put all three in
// his mouth at once, and two of them are ours to go and find first.
//
// Closing is a record, not a deduction. It writes nothing to payroll.
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronRight, FileText, Upload, Undo2, Info } from 'lucide-react'
import {
  useGetDriverCashOutstanding,
  useUploadCashDocument,
  useCloseCashItem,
  useWithdrawCashItemClosure,
  useGetCashDocumentUrl,
} from '../../hooks/backend/cash'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip, type ChipTone } from '../interviews/Chip'
import { zar, num } from './cashModel'

type Row = Record<string, any>
type Kind = 'shortfall' | 'deposit' | 'petrol'
type Show = 'open' | 'closed' | 'all'

const KINDS: Array<{ kind: Kind; label: string; help: string; tone: ChipTone }> = [
  {
    kind: 'shortfall',
    label: 'Not accounted for',
    help: 'Cash he received on a shift that his declaration does not account for at all. Only shifts with both form parts on file.',
    tone: 'red',
  },
  {
    kind: 'deposit',
    label: 'Deposits not on the statement',
    help: 'Deposits he declared that the Fleetly bank statement does not show. Check for a combined deposit or another account before asking him.',
    tone: 'amber',
  },
  {
    kind: 'petrol',
    label: 'Petrol with no proof',
    help: 'Cash petrol he declared that no fuel report or receipt supports. Most of these are fills the report never recorded a payment method for.',
    tone: 'neutral',
  },
]

const OUTCOMES: Array<{ value: string; label: string }> = [
  { value: 'deposited', label: 'It was deposited (deposit slip)' },
  { value: 'petrol', label: 'It was spent on petrol (receipt)' },
  { value: 'other_spend', label: 'Another approved spend (receipt)' },
  { value: 'handed_over', label: 'He handed it over (signed note)' },
]

function defaultOutcome(kind: Kind): string {
  return kind === 'petrol' ? 'petrol' : 'deposited'
}

// A Postgres date reaches the browser as 2026-10-24T00:00:00.000Z. Taking the
// first ten characters is the date Postgres held; new Date() would move it a day
// in some time zones.
function day(v: unknown): string {
  return String(v ?? '').slice(0, 10)
}

function dayLabel(v: unknown): string {
  const d = day(v)
  if (!d) return '—'
  return new Date(`${d}T12:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })
}

function monthLabel(v: unknown): string {
  const d = day(v)
  if (!d) return '—'
  return new Date(`${d.slice(0, 7)}-15T12:00:00`).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

function CloseForm({
  item,
  busy,
  onSubmit,
  onCancel,
}: {
  item: Row
  busy: boolean
  onSubmit: (args: {
    file: File
    outcome: string
    amount: number
    documentAmount: number
    docDate: string
    note: string
  }) => void
  onCancel: () => void
}) {
  const open = Number(item.outstanding_zar ?? 0)
  const [outcome, setOutcome] = useState(defaultOutcome(item.item_kind as Kind))
  const [file, setFile] = useState<File | null>(null)
  const [amount, setAmount] = useState(open.toFixed(2))
  const [documentAmount, setDocumentAmount] = useState(open.toFixed(2))
  const [docDate, setDocDate] = useState(day(item.shift_date))
  const [note, setNote] = useState('')

  const amt = Number(amount)
  const docAmt = Number(documentAmount)
  const ready = file !== null && amt > 0 && amt <= open + 0.005 && docAmt >= amt && docDate !== ''

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label className="text-[11px]">What happened to the money</Label>
          <NativeSelect value={outcome} disabled={busy} onChange={(e) => setOutcome(e.target.value)}>
            {OUTCOMES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">The slip, receipt or note (photo or PDF)</Label>
          <Input
            type="file"
            accept="image/*,application/pdf"
            disabled={busy}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="space-y-1">
          <Label className="text-[11px]">Amount printed on the document</Label>
          <Input
            type="number"
            step="0.01"
            value={documentAmount}
            disabled={busy}
            onChange={(e) => setDocumentAmount(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Of which for this shift (open {zar(open)})</Label>
          <Input type="number" step="0.01" value={amount} disabled={busy} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Date on the document</Label>
          <Input type="date" value={docDate} disabled={busy} onChange={(e) => setDocDate(e.target.value)} />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[11px]">Note (optional)</Label>
        <Input
          value={note}
          disabled={busy}
          placeholder="For example: one slip for the 21st and the 24th together."
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {docAmt > amt + 0.005 && (
        <p className="text-[11px] text-muted-foreground">
          The rest of this document, {zar(docAmt - amt)}, stays available. Upload the same file on the next shift it
          covers.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={busy || !ready}
          onClick={() =>
            file &&
            onSubmit({ file, outcome, amount: amt, documentAmount: docAmt, docDate, note: note.trim() })
          }
        >
          {busy ? <Spinner className="mr-1.5 h-3.5 w-3.5" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
          Upload and close
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <span className="text-[11px] text-muted-foreground">
          A record, not a deduction. It changes no salary.
        </span>
      </div>
    </div>
  )
}

export function DriverOutstandingPanel({
  driverId,
  driverName,
  mayClose,
  onChanged,
}: {
  driverId: string
  driverName: string
  mayClose: boolean
  onChanged?: () => void
}) {
  const list = useGetDriverCashOutstanding()
  const upload = useUploadCashDocument()
  const close = useCloseCashItem()
  const withdraw = useWithdrawCashItemClosure()
  const docUrl = useGetCashDocumentUrl()

  const [kind, setKind] = useState<Kind | null>(null)
  const [show, setShow] = useState<Show>('open')
  const [openItem, setOpenItem] = useState<string | null>(null)
  const [closing, setClosing] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [withdrawing, setWithdrawing] = useState<string | null>(null)
  const [withdrawReason, setWithdrawReason] = useState('')

  const load = useCallback(() => {
    list.trigger({ driver_id: driverId }, { skipCache: true })
    // The hook objects are recreated each render; depending on them would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId])

  useEffect(() => {
    load()
  }, [load])

  const data = list.data as { items?: Row[]; attributed?: Row | null } | undefined
  const items = useMemo(() => (Array.isArray(data?.items) ? data.items : []), [data])
  const attributed = data?.attributed ?? null

  const totals = useMemo(() => {
    const out: Record<Kind, { open: number; count: number; closed: number }> = {
      shortfall: { open: 0, count: 0, closed: 0 },
      deposit: { open: 0, count: 0, closed: 0 },
      petrol: { open: 0, count: 0, closed: 0 },
    }
    for (const r of items) {
      const k = r.item_kind as Kind
      if (!out[k]) continue
      const o = Number(r.outstanding_zar ?? 0)
      if (o > 0) {
        out[k].open += o
        out[k].count += 1
      } else {
        out[k].closed += 1
      }
    }
    return out
  }, [items])

  const shown = useMemo(
    () =>
      items.filter((r) => {
        if (kind && r.item_kind !== kind) return false
        const isOpen = Number(r.outstanding_zar ?? 0) > 0
        return show === 'all' || (show === 'open' ? isOpen : !isOpen)
      }),
    [items, kind, show],
  )

  // Grouped by month so a long history reads as months, newest first.
  const months = useMemo(() => {
    const out: Array<{ month: string; rows: Row[]; open: number }> = []
    for (const r of shown) {
      const m = day(r.shift_month).slice(0, 7)
      let g = out[out.length - 1]
      if (!g || g.month !== m) {
        g = { month: m, rows: [], open: 0 }
        out.push(g)
      }
      g.rows.push(r)
      g.open += Number(r.outstanding_zar ?? 0)
    }
    return out
  }, [shown])

  const submitClose = async (
    item: Row,
    args: { file: File; outcome: string; amount: number; documentAmount: number; docDate: string; note: string },
  ) => {
    setBusy(true)
    try {
      const contentBase64 = await readAsBase64(args.file)
      const kindOfDoc =
        args.outcome === 'deposited' ? 'deposit_slip' : args.outcome === 'petrol' ? 'fuel_receipt' : 'other'
      const up = (await upload.trigger(
        {
          kind: kindOfDoc,
          driver_id: driverId,
          shift_id: String(item.shift_id),
          filename: args.file.name,
          contentType: args.file.type || 'application/octet-stream',
          contentBase64,
        },
        { skipCache: true },
      ).result) as Row
      if (!up?.document_id) {
        toast.error('The document was not stored', {
          description: String(up?.note ?? up?.error ?? 'Try the upload again.'),
        })
        return
      }
      const res = (await close.trigger(
        {
          document_id: String(up.document_id),
          item_kind: String(item.item_kind),
          item_ref: String(item.item_kind === 'deposit' ? item.cash_transaction_id : item.shift_id),
          outcome: args.outcome,
          amount_zar: args.amount,
          document_amount_zar: args.documentAmount,
          doc_date: args.docDate,
          note: args.note || undefined,
        },
        { skipCache: true },
      ).result) as Row
      if (res?.ok) {
        const read = up?.reading as Row | undefined
        const readAmount = read?.amount_zar === null || read?.amount_zar === undefined ? null : Number(read.amount_zar)
        // pay_effect is the database's sentence about pay: a refund raised because
        // the payslip that deducted this cash is closed, or what comes off a payslip
        // still open. Null for petrol and deposits, which no payslip deducted.
        const payEffect = (res.pay_effect as Row | null | undefined)?.message
        const notes = [
          readAmount !== null && Math.abs(readAmount - args.documentAmount) > 0.5
            ? `The document reads as ${zar(readAmount)} and you entered ${zar(args.documentAmount)}. Open it and check.`
            : null,
          payEffect ? String(payEffect) : null,
        ].filter(Boolean)
        toast.success(
          Number(res.outstanding_zar ?? 0) > 0 ? `Recorded. ${zar(res.outstanding_zar)} is still open on this shift.` : 'Recorded. This item is closed.',
          notes.length ? { description: notes.join(' ') } : undefined,
        )
        setClosing(null)
        load()
        onChanged?.()
      } else {
        // The file is stored and stays on the Documents tab as unconfirmed, so a
        // refusal here loses nothing; the manager can confirm it there or retry.
        toast.error('Not closed', { description: String(res?.error ?? '') })
      }
    } catch (err) {
      toast.error('Not closed', { description: err instanceof Error ? err.message : String(err) })
    } finally {
      setBusy(false)
    }
  }

  const submitWithdraw = async (closureId: string) => {
    const res = (await withdraw.trigger({ closure_id: closureId, reason: withdrawReason.trim() }, { skipCache: true })
      .result) as Row
    if (res?.ok) {
      toast.success('Withdrawn. The item is open again.', res.message ? { description: String(res.message) } : undefined)
      setWithdrawing(null)
      setWithdrawReason('')
      load()
      onChanged?.()
    } else {
      toast.error('Not withdrawn', { description: String(res?.error ?? '') })
    }
  }

  const openDocument = async (documentId: string) => {
    const res = (await docUrl.trigger({ document_id: documentId }, { skipCache: true }).result) as Row
    if (res?.url) window.open(String(res.url), '_blank', 'noopener')
    else toast.error('That document could not be opened.')
  }

  const first = driverName.split(' ')[0]

  return (
    <div className="mb-3 space-y-3 rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-semibold text-foreground">Where {first}&rsquo;s cash is outstanding</span>
        <span className="text-xs text-muted-foreground">every month on file, not only the month above</span>
      </div>

      {list.error && !data ? (
        <p className="text-xs text-rose-700 dark:text-rose-300">
          His items could not be loaded. {String(list.error)}{' '}
          <button type="button" className="underline" onClick={load}>
            Try again
          </button>
        </p>
      ) : !data ? (
        // Until the first answer arrives there is nothing to show, and three R0.00
        // tiles over "Nothing outstanding" would be a missing figure read as zero.
        <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
          <Spinner className="h-3.5 w-3.5" /> Loading his items…
        </div>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-3">
            {KINDS.map((k) => {
              const t = totals[k.kind]
              const active = kind === k.kind
              return (
                <button
                  key={k.kind}
                  type="button"
                  onClick={() => setKind(active ? null : k.kind)}
                  aria-pressed={active}
                  className={`rounded-md border px-3 py-2 text-left transition-colors hover:bg-accent/50 ${
                    active ? 'border-[hsl(var(--brand))] ring-1 ring-[hsl(var(--brand))]' : 'border-border'
                  }`}
                >
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</div>
                  <div className="text-lg font-semibold tabular-nums text-foreground">{zar(t.open)}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {num(t.count)} open{t.closed > 0 ? `, ${num(t.closed)} closed` : ''}
                  </div>
                </button>
              )
            })}
          </div>

          <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            {kind ? KINDS.find((k) => k.kind === kind)?.help : 'These three are different questions and are never added together. Click one to see only those.'}
          </p>

          {attributed && Number(attributed.credits) > 0 && (
            <p className="rounded-md border border-dashed border-border px-3 py-2 text-[11px] text-muted-foreground">
              {zar(attributed.amount_zar)} in {num(attributed.credits)} bank credit
              {Number(attributed.credits) === 1 ? '' : 's'} carry his name with no declared deposit behind them. Some
              of the deposits below may be those. They are on the credit panel underneath.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <NativeSelect className="w-40" value={show} onChange={(e) => setShow(e.target.value as Show)} aria-label="Show">
              <option value="open">Open items</option>
              <option value="closed">Closed items</option>
              <option value="all">All items</option>
            </NativeSelect>
            <span className="text-xs text-muted-foreground">{num(shown.length)} shown</span>
            {list.loading && <Spinner className="h-3.5 w-3.5" />}
          </div>

          {shown.length === 0 ? (
            <p className="p-3 text-center text-xs text-muted-foreground">
              {show === 'open' ? 'Nothing outstanding here.' : 'Nothing to show.'}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Shift</th>
                    <th className="px-3 py-2 text-left font-medium">Car</th>
                    <th className="px-3 py-2 text-right font-medium">Cash he collected</th>
                    <th className="px-3 py-2 text-left font-medium">What is open</th>
                    <th className="px-3 py-2 text-right font-medium">He said</th>
                    <th className="px-3 py-2 text-right font-medium">Shown</th>
                    <th className="px-3 py-2 text-right font-medium">Open</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {months.map((g) => (
                    <Fragment key={g.month}>
                      <tr className="border-t border-border bg-muted/30">
                        <td colSpan={6} className="px-3 py-1.5 font-medium text-foreground">
                          {monthLabel(`${g.month}-01`)}
                        </td>
                        <td className="px-3 py-1.5 text-right font-medium tabular-nums">{zar(g.open)}</td>
                        <td />
                      </tr>
                      {g.rows.map((r) => {
                        const id = String(r.item_id)
                        const k = KINDS.find((x) => x.kind === r.item_kind)
                        const isOpen = openItem === id
                        const outstanding = Number(r.outstanding_zar ?? 0)
                        const closures = Array.isArray(r.closed_with) ? (r.closed_with as Row[]) : []
                        return (
                          <Fragment key={id}>
                            <tr className="border-t border-border">
                              <td className="whitespace-nowrap px-3 py-1.5">
                                <button
                                  type="button"
                                  className="flex items-center gap-1 hover:underline"
                                  onClick={() => setOpenItem(isOpen ? null : id)}
                                  aria-expanded={isOpen}
                                >
                                  <ChevronRight
                                    className={`h-3 w-3 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                                  />
                                  {dayLabel(r.shift_date)}
                                </button>
                              </td>
                              <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">
                                {r.vehicle_registration ?? '—'}
                              </td>
                              <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                                {r.cash_received_zar === null || r.cash_received_zar === undefined ? (
                                  <span className="text-muted-foreground">not known</span>
                                ) : (
                                  <>
                                    {zar(r.cash_received_zar)}
                                    {Number(r.uber_cash_trips) > 0 && (
                                      <span className="ml-1 text-muted-foreground">({num(r.uber_cash_trips)} trips)</span>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className="px-3 py-1.5">
                                <div className="flex flex-wrap items-center gap-1">
                                  <Chip tone={k?.tone ?? 'neutral'}>{k?.label ?? String(r.item_kind)}</Chip>
                                  {r.evidence_not_loaded && <Chip tone="neutral">our records missing</Chip>}
                                  {closures.length > 0 && (
                                    <Chip tone="green">
                                      {closures.length} document{closures.length === 1 ? '' : 's'}
                                    </Chip>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{zar(r.claimed_zar)}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                                {zar(Number(r.proven_zar ?? 0) + Number(r.evidenced_zar ?? 0))}
                              </td>
                              <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                                {outstanding > 0 ? zar(outstanding) : <span className="text-muted-foreground">closed</span>}
                              </td>
                              <td className="whitespace-nowrap px-3 py-1.5 text-right">
                                {mayClose && outstanding > 0 && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => {
                                      setOpenItem(id)
                                      setClosing(id)
                                    }}
                                  >
                                    <Upload className="mr-1 h-3 w-3" />
                                    Add proof
                                  </Button>
                                )}
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="border-t border-border bg-muted/10">
                                <td colSpan={8} className="space-y-2 px-3 py-2">
                                  <p className="text-[11px] text-muted-foreground">{r.source_note}</p>
                                  {closures.length > 0 && (
                                    <ul className="space-y-1">
                                      {closures.map((c) => (
                                        <li key={String(c.closure_id)} className="flex flex-wrap items-center gap-2 text-[11px]">
                                          <span className="font-medium">
                                            {OUTCOMES.find((o) => o.value === c.outcome)?.label ?? String(c.outcome)}
                                          </span>
                                          <span className="tabular-nums">{zar(c.amount_zar)}</span>
                                          <span className="text-muted-foreground">
                                            {c.closed_by_name} ·{' '}
                                            {new Date(String(c.closed_at)).toLocaleDateString('en-ZA')}
                                          </span>
                                          <button
                                            type="button"
                                            className="inline-flex items-center gap-1 underline"
                                            onClick={() => openDocument(String(c.document_id))}
                                          >
                                            <FileText className="h-3 w-3" />
                                            {c.file_name ?? 'document'}
                                          </button>
                                          {c.note && <span className="text-muted-foreground">{c.note}</span>}
                                          {c.refund ? (
                                            <Chip tone="amber">
                                              Refund {String((c.refund as Row).ref)}, {zar((c.refund as Row).amount_zar)},{' '}
                                              {String((c.refund as Row).status)}
                                            </Chip>
                                          ) : null}
                                          {mayClose &&
                                            (withdrawing === String(c.closure_id) ? (
                                              <span className="flex items-center gap-1">
                                                <Input
                                                  className="h-7 w-56 text-xs"
                                                  placeholder="Why was this wrong?"
                                                  value={withdrawReason}
                                                  onChange={(e) => setWithdrawReason(e.target.value)}
                                                />
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="h-7 px-2 text-xs"
                                                  disabled={withdraw.loading || withdrawReason.trim() === ''}
                                                  onClick={() => submitWithdraw(String(c.closure_id))}
                                                >
                                                  Withdraw
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  className="h-7 px-2 text-xs"
                                                  onClick={() => setWithdrawing(null)}
                                                >
                                                  Cancel
                                                </Button>
                                              </span>
                                            ) : (
                                              <button
                                                type="button"
                                                className="inline-flex items-center gap-1 text-muted-foreground underline"
                                                onClick={() => {
                                                  setWithdrawing(String(c.closure_id))
                                                  setWithdrawReason('')
                                                }}
                                              >
                                                <Undo2 className="h-3 w-3" />
                                                withdraw
                                              </button>
                                            ))}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {closing === id && (
                                    <CloseForm
                                      item={r}
                                      busy={busy}
                                      onCancel={() => setClosing(null)}
                                      onSubmit={(args) => submitClose(r, args)}
                                    />
                                  )}
                                  {mayClose && outstanding > 0 && closing !== id && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 px-2 text-xs"
                                      onClick={() => setClosing(id)}
                                    >
                                      <Upload className="mr-1 h-3 w-3" />
                                      Add proof for this shift
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        )
                      })}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!mayClose && (
            <p className="text-[11px] text-muted-foreground">
              Closing an item needs Edit drivers or Edit fleet on your Cockpit access. You can still add his slips and
              receipts below, and whoever manages him can close the items.
            </p>
          )}
        </>
      )}
    </div>
  )
}
